import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LargeSegmentedControl, SegmentedControlOption } from 'ui/segmentedControl';
import { fetchPerformantCampaignRequest } from 'modules/iterableGoals/ducks/actions';
import { useTabContext } from 'modules/iterableGoals/contexts';
import { getPerformantCampaignState } from 'modules/iterableGoals/ducks/selectors';
import { useGoalsTranslations } from 'modules/iterableGoals/hooks';
import {
  AnalyticsTabs,
  PerformantCampaignVariant,
  TabSectionPropType,
} from 'modules/iterableGoals/types';
import { TAB_ACTIONS, useRumAction } from 'services/observability/datadog';
import { SectionTitle } from '../../SectionTitle';
import { AnalyticsCard } from '../AnalyticsCard';
import { StyledSelectControlWrapper, SubTitleLink } from './Styled';
import { PerformantList } from './List';

const NUM_OF_PERFORMANT_CAMPAIGNS = 4;

export const CampaignPerformance = ({
  isIterableGoalsStretchFlagOn,
  loading,
  error,
  goalName,
  startTime,
  endTime,
}: TabSectionPropType) => {
  const addSignal = useRumAction(TAB_ACTIONS);
  const [campaignVariant, setCampaignVariant] = useState(PerformantCampaignVariant.BEST);
  const { setSelectedTab } = useTabContext();
  const dispatch = useDispatch();
  const performantCampaignState = useSelector(getPerformantCampaignState);
  const { t } = useGoalsTranslations();
  const selectControlOptions: SegmentedControlOption[] = [
    {
      value: PerformantCampaignVariant.BEST,
      name: t('labels.performantCampaigns.best'),
    },
    {
      value: PerformantCampaignVariant.WORST,
      name: t('labels.performantCampaigns.worst'),
    },
  ];

  const fetchPerformantCampaignData = (
    goalIdentifier?: string,
    analyticsStartTime?: string,
    analyticsEndTime?: string,
  ) => {
    if (!goalIdentifier || !analyticsStartTime || !analyticsEndTime) return;

    dispatch(
      fetchPerformantCampaignRequest({
        goalName: goalIdentifier,
        count: NUM_OF_PERFORMANT_CAMPAIGNS,
        startTime: analyticsStartTime,
        endTime: analyticsEndTime,
      }),
    );
  };

  /**
   * Fetch new performant campaign data as required
   */
  useEffect(() => {
    fetchPerformantCampaignData(goalName, startTime, endTime);
  }, [goalName, startTime, endTime]);

  return (
    <>
      <SectionTitle
        title={t(
          campaignVariant === PerformantCampaignVariant.BEST
            ? 'title.analytics.bestPerformingCampaigns'
            : 'title.analytics.worstPerformingCampaigns',
        )}
        subTitle={
          isIterableGoalsStretchFlagOn ? (
            <SubTitleLink
              data-test="performant-campaign-sub-title"
              onClick={() => setSelectedTab(AnalyticsTabs.CAMPAIGNS)}
            >
              {t('title.analytics.titleLink')}
            </SubTitleLink>
          ) : (
            <></>
          )
        }
        sectionComponent={
          <StyledSelectControlWrapper data-test="performant-campaign-select-controller">
            <LargeSegmentedControl
              options={selectControlOptions}
              onSelect={(value) => {
                setCampaignVariant(value);
                addSignal('PERFORMANT_CAMPAIGN_TOGGLED');
              }}
              value={campaignVariant}
            />
          </StyledSelectControlWrapper>
        }
      />
      <AnalyticsCard
        noPadding
        height={loading || error ? 432 : 'auto'}
        loading={loading}
        error={error}
        handleReload={() => fetchPerformantCampaignData(goalName, startTime, endTime)}
      >
        <PerformantList
          campaignVariant={campaignVariant}
          data={performantCampaignState.data[campaignVariant]}
        ></PerformantList>
      </AnalyticsCard>
    </>
  );
};
